import React from 'react';
import { Card, CardContent, Skeleton, Stack, Typography, Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useMotionPresets } from '../../theme/motion';

const resolveThemeColor = (theme, color) => {
  if (!color) return theme.palette.primary.main;
  if (typeof color !== 'string') return color;
  if (!color.includes('.')) return color;

  const [paletteKey, shadeKey] = color.split('.');
  const palette = theme.palette?.[paletteKey];
  if (palette && typeof palette === 'object' && shadeKey in palette) {
    return palette[shadeKey];
  }
  return color;
};

export default function StatCard({
  icon,
  title,
  value,
  hint,
  color = 'primary.main',
  loading = false,
  onClick,
  tone = 'default',
}) {
  const motionPresets = useMotionPresets();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const isDashboard = tone === 'dashboard';
  const accent = resolveThemeColor(theme, color);

  const cardSx = isDashboard
    ? {
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid',
        borderColor: theme.palette.divider,
        backgroundColor: alpha(theme.palette.background.paper, dark ? 0.78 : 0.92),
        backdropFilter: 'blur(12px)',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: theme.shadows[2],
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          height: 3,
          width: '100%',
          background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.25)})`,
        },
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] } : undefined,
      }
    : {
        borderRadius: 2,
        borderLeft: '3px solid',
        borderLeftColor: color,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { transform: 'translateY(-1px)', boxShadow: 4 } : undefined,
      };

  return (
    <Card
      component={motion.div}
      {...motionPresets.scaleIn}
      onClick={onClick}
      sx={cardSx}
    >
      <CardContent>
        {loading ? (
          <Stack spacing={0.7}>
            <Skeleton variant="text" width="46%" />
            <Skeleton variant="text" width="32%" height={34} />
          </Stack>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.4 }}>
            {icon ? (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: isDashboard ? alpha(accent, dark ? 0.22 : 0.12) : 'action.hover',
                  color: isDashboard ? accent : color,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.2 }}>
                {value}
              </Typography>
              {hint ? (
                <Typography variant="caption" color="text.secondary">
                  {hint}
                </Typography>
              ) : null}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

